import { IQuick3DCamera, TCameraUpdateEvent } from './quick3d-camera-interface';
import {
    Vector3,
    Box3,
    PerspectiveCamera,
    Plane,
    Raycaster,
    Quaternion,
    Box3Helper,
} from 'three';
import { makeArrayOfObjects } from '../utils/camera-utils';

export interface IQuick3DPerspectiveCameraArguments {
    /**
     * ratio of view with to view height. usually calculated from dimensions of the render target
     */
    aspect?: number;

    /**
     * max view distance of the camera. objects further than this will be clipped
     */
    far?: number;

    /**
     * vertical viewing angle
     */
    fieldOfView?: number;

    /**
     * nearest view distance of the camera. objects closer than this will be clipped
     */
    near?: number;
}

/**
 * used to render scenes with a perspective (such as first person) projection
 *
 * [perspective projection](https://en.wikipedia.org/wiki/3D_projection#Perspective_projection)
 *
 * [THREE.PerspectiveCamera documentation](https://threejs.org/docs/#api/en/cameras/PerspectiveCamera)
 */
export class Quick3DPerspectiveCamera
    extends PerspectiveCamera
    implements IQuick3DCamera
{
    // private aspect: number;

    private boxCorners: Array<Vector3>;
    private boxSize: Vector3;
    private cameraLookDirection: Vector3;
    private cameraPositionOnPlane: Vector3;
    private closestPlaneToCamera: Plane;
    private inverseRotation: Quaternion;
    private newWorldPosition: Vector3;
    private parentWorldPosition: Vector3;
    private projectedBox: Box3;
    private projectedPoint: Vector3;
    private rayCaster: Raycaster;
    private updateEvent: TCameraUpdateEvent;
    private worldXAxis: Vector3;
    private worldYAxis: Vector3;
    private worldZAxis: Vector3;

    // public get aspect(): number { return this._aspect; }
    public get horizontalFOV(): number {
        return (this.horizontalFOVRadians * 180) / Math.PI;
    }
    //calculation from: https://wiki.panotools.org/Field_of_View
    public get horizontalFOVRadians(): number {
        return (
            2 * Math.atan(Math.tan(this.verticalFOVRadians * 0.5) * this.aspect)
        );
    }
    public get verticalFOV(): number {
        return this.fov;
    }
    public get verticalFOVRadians(): number {
        return (this.fov * Math.PI) / 180;
    }

    // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
    // public set aspect(newAspect: number) {
    //     if (newAspect) {
    //         this._aspect = newAspect;
    //         this.updateProjectionMatrix();
    //     }
    // }

    /**
     * @param cameraArguments.aspect defaults to 1
     * @param cameraArguments.far defaults to 5000
     * @param cameraArguments.fieldOfView defaults to 1
     * @param cameraArguments.near defaults to 0.1
     */
    public constructor(
        cameraArguments: IQuick3DPerspectiveCameraArguments = {}
    ) {
        const {
            aspect = 1,
            far = 10000,
            fieldOfView = 90,
            near = 0.1,
        } = cameraArguments;

        super(fieldOfView, 0, near, far);
        this.aspect = aspect;

        this.boxCorners = makeArrayOfObjects<Vector3>(8, Vector3);
        this.boxSize = new Vector3();

        this.cameraLookDirection = new Vector3();
        this.cameraPositionOnPlane = new Vector3();
        this.closestPlaneToCamera = new Plane();
        this.getWorldDirection(this.cameraLookDirection);

        this.inverseRotation = this.getWorldQuaternion(
            new Quaternion()
        ).invert();

        this.newWorldPosition = new Vector3();

        this.parentWorldPosition = new Vector3();
        this.projectedBox = new Box3();
        this.projectedPoint = new Vector3();

        this.rayCaster = new Raycaster(
            this.getWorldPosition(new Vector3()),
            this.cameraLookDirection,
            this.near,
            this.far
        );

        this.worldXAxis = new Vector3();
        this.worldYAxis = new Vector3();
        this.worldZAxis = new Vector3();
        this.matrixWorld.extractBasis(
            this.worldXAxis,
            this.worldYAxis,
            this.worldZAxis
        );

        this.updateEvent = {
            type: 'cameraUpdateEvent',
            camera: this,
        };
    }

    /**
     * re-orients the camera such that the center of the box is in the middle of the screen
     *
     * [THREE.Box3 documentation](https://threejs.org/docs/#api/en/math/Box3)
     * @param area 3D volume the camera should attempt to fit around. for example, the bounding box of a model, or of an entire scene of objects.
     */
    // public fit(area: Box3): void {
    public fit(area: Box3): Box3Helper {
        if (area.isEmpty()) {
            return;
        }

        this.zoom = 1;
        this.updateProjectionMatrix();
        this.updateMatrixWorld(false);

        // set raycaster to align with camera look vector and postion
        this.matrixWorld.extractBasis(
            this.worldXAxis,
            this.worldYAxis,
            this.worldZAxis
        );
        this.cameraLookDirection.copy(this.worldZAxis).negate();
        // put raycaster far away to be sure the whole model is within view
        area.getCenter(this.newWorldPosition).add(
            this.worldZAxis.setLength(area.getSize(this.boxSize).lengthSq())
        );
        this.rayCaster.near = this.near;
        this.rayCaster.far = this.far;
        this.rayCaster.set(
            this.newWorldPosition,
            this.cameraLookDirection.normalize()
        );

        this.boxCorners[0].set(area.min.x, area.min.y, area.min.z);
        this.boxCorners[1].set(area.min.x, area.min.y, area.max.z);
        this.boxCorners[2].set(area.min.x, area.max.y, area.min.z);
        this.boxCorners[3].set(area.min.x, area.max.y, area.max.z);
        this.boxCorners[4].set(area.max.x, area.min.y, area.min.z);
        this.boxCorners[5].set(area.max.x, area.min.y, area.max.z);
        this.boxCorners[6].set(area.max.x, area.max.y, area.min.z);
        this.boxCorners[7].set(area.max.x, area.max.y, area.max.z);

        // closest point on cube facing the direction camera will be placed
        let closestPointToCamera = this.boxCorners[0];
        let currentDistance = closestPointToCamera.distanceToSquared(
            this.newWorldPosition
        );
        for (let i = 1; i < 8; ++i) {
            const dist = this.boxCorners[i].distanceToSquared(
                this.newWorldPosition
            );
            if (dist < currentDistance) {
                currentDistance = dist;
                closestPointToCamera = this.boxCorners[i];
            }
        }

        // build plane from found point
        this.closestPlaneToCamera.setFromNormalAndCoplanarPoint(
            this.worldZAxis.normalize(),
            closestPointToCamera
        );

        // calculate visible bounds
        const rightAngleBoxCorners = []
        for (const corner of this.boxCorners) {
            const origin  = new Vector3().copy(corner);
            corner.copy(
                this.closestPlaneToCamera.projectPoint(
                    corner,
                    this.projectedPoint
                )
            )
            // rightAngleBoxCorners.push(corner.clone());
            // const perspectiveCorner = new Vector3().subVectors(corner, origin).multiplyScalar(Math.tan(this.verticalFOVRadians * 0.5));
            // corner.copy(perspectiveCorner);
        }
        this.projectedBox.setFromPoints(this.boxCorners);
        this.projectedBox.getSize(this.boxSize);

        // const rightAngleBox = new Box3().setFromPoints(rightAngleBoxCorners);
        // const planeCenter = rightAngleBox.getCenter(new Vector3());
        // const rightAngleBoxSize = new Vector3();
        // rightAngleBox.getSize(rightAngleBoxSize);

        const adjacentFromVerticalFOV = this.getFrustumDistanceAtHeight(
            this.worldYAxis.clone().multiply(this.boxSize).length()
        );
        const adjacentFromHorizontalFOV = this.getFrustumDistanceAtWidth(
            this.worldXAxis.clone().multiply(this.boxSize).length()
        );

        const rightAngleHeight = this.getFrustumDistanceAtHeight(
            this.worldYAxis.clone().multiply(this.boxSize).length() * Math.tan(this.verticalFOVRadians * 0.5)
        );
        const rightAngleWidth = this.getFrustumDistanceAtWidth(
            this.worldXAxis.clone().multiply(this.boxSize).length() * Math.tan(this.horizontalFOVRadians * 0.5)
        );
        const refOffset = this.worldZAxis.clone()
        .multiplyScalar(Math.max(adjacentFromVerticalFOV, adjacentFromHorizontalFOV));

        // dimension that takes up the most screen space
        const largestAdjacent = Math.max(
            rightAngleHeight,
            rightAngleWidth
        );

        // position camera minimum distance away to fit largest box dimension
        const hitResult = this.rayCaster.ray.intersectPlane(
            this.closestPlaneToCamera,
            this.cameraPositionOnPlane
        );
        const offset = this.worldZAxis.clone().multiplyScalar(largestAdjacent);
        // console.log(offset);
        const offsetSubLength = refOffset.sub(offset).length() * Math.tan(this.verticalFOVRadians * 0.5);
        const downOffset = this.worldYAxis.clone().multiplyScalar(offsetSubLength);
        // console.log(downOffset);
        if (hitResult) {
            if (this.parent) {
                // convert calculations to local space
                this.getWorldQuaternion(this.inverseRotation).invert();
                offset.applyQuaternion(this.inverseRotation);
                this.parent.getWorldPosition(this.parentWorldPosition);
                hitResult.sub(this.parentWorldPosition);
                hitResult.applyQuaternion(this.inverseRotation);
            }
            this.position.addVectors(hitResult, offset);
            this.position.sub(downOffset);
        } else {
            console.warn(
                'Quick3DPerspectiveCamera::Fit - failed to calculate min distance from box.'
            );
        }

        this.updateProjectionMatrix();
        this.updateMatrixWorld(false);

        return new Box3Helper(area);
    }

    public getFrustumDistanceAtHeight(height: number): number {
        return (height * 0.5) / Math.tan(this.verticalFOVRadians * 0.5);
    }

    public getFrustumDistanceAtWidth(width: number): number {
        return (width * 0.5) / Math.tan(this.horizontalFOVRadians * 0.5);
    }

    public getFrustumHeightAtDistance(distance: number): number {
        return 2 * distance * Math.tan(this.verticalFOVRadians * 0.5);
    }

    public getFrustumWidthAtDistance(distance: number): number {
        return 2 * distance * Math.tan(this.horizontalFOVRadians * 0.5);
    }

    /**
     * currently documented function for updating world matrices.
     * has performance issues, better to use updateWorldMatrix instead
     *
     * [see issue on git](https://github.com/mrdoob/three.js/pull/16292)
     *
     * overridden to emit cameraUpdateEvent event
     *
     * @param force perform calculations regardless if needed
     */
    public override updateMatrixWorld(force: boolean): void {
        if (this.matrixAutoUpdate) {
            this.updateMatrix();
        }

        if (this.matrixWorldNeedsUpdate || force) {
            if (!this.parent) {
                this.matrixWorld.copy(this.matrix);
            } else {
                this.matrixWorld.multiplyMatrices(
                    this.parent.matrixWorld,
                    this.matrix
                );
            }
            this.matrixWorldNeedsUpdate = false;
            force = true;

            this.dispatchEvent(this.updateEvent);
        }

        // update children
        const children = this.children;
        for (let i = 0, l = children.length; i < l; ++i) {
            children[i].updateMatrixWorld(force);
        }

        this.matrixWorldInverse.copy(this.matrixWorld).invert();
    }

    /**
     * proposed version of updateMatrixWorld that exists but is undocumented
     *
     * [see issue on git](https://github.com/mrdoob/three.js/pull/16292)
     *
     * overridden to emit cameraUpdateEvent event
     * @param [updateParents] boolean
     * @param [updateChildren] boolean
     */
    public override updateWorldMatrix(
        updateParents = false,
        updateChildren = true
    ): void {
        this.dispatchEvent(this.updateEvent);
        super.updateWorldMatrix(updateParents, updateChildren);
    }

    /**
     * overridden to emit cameraUpdateEvent event
     */
    public override updateProjectionMatrix(): void {
        this.dispatchEvent(this.updateEvent);
        super.updateProjectionMatrix();
    }
}
